import Header from "units/dashboard/components/Header";
import AdminSidebar from "./components/AdminSidebar";
import Footer from "units/dashboard/components/Footer";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  CreditCard,
  RefreshCw,
  Upload,
  X,
  FileImage,
  Loader2,
  Search,
  ChevronRight,
  ChevronLeft,
} from "lucide-react";
import {
  getPendingSettlements,
  uploadSettlementReceipt,
  approveSettlement,
} from "./services/SettlementRequestsListService";
import { toast } from "react-toastify";
import axiosInstance from "api/axiosInstance";

const SettlementRequestsList = () => {
  const accessToken = useSelector(
    (state) => state.auth.accessToken
  );

  // =========================
  // Pending Requests
  // =========================

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");

  // =========================
  // History
  // =========================

  const [historyRequests, setHistoryRequests] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const [historySearchTerm, setHistorySearchTerm] =
    useState("");

  const [historyStatusFilter, setHistoryStatusFilter] =
    useState("all");

  const [historyTypeFilter, setHistoryTypeFilter] =
    useState("all");

  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize] = useState(10);

  // =========================
  // Modals
  // =========================

  const [selectedRequest, setSelectedRequest] =
    useState(null);

  const [approveModal, setApproveModal] =
    useState(false);

  const [selectedFile, setSelectedFile] =
    useState(null);

  const [receiptImageUrl, setReceiptImageUrl] =
    useState("");

  const [paymentInfoText, setPaymentInfoText] =
    useState("");

  const [uploadingReceipt, setUploadingReceipt] =
    useState(false);

  const [approving, setApproving] =
    useState(false);

  const [receiptModal, setReceiptModal] =
    useState(false);

  const [selectedReceiptUrl, setSelectedReceiptUrl] =
    useState("");

  // =========================
  // Receipt URL
  // =========================

  const getReceiptUrl = (receiptPath) => {
    if (!receiptPath) return "";

    if (
      receiptPath.startsWith("http://") ||
      receiptPath.startsWith("https://")
    ) {
      return receiptPath;
    }

    const apiBase = axiosInstance?.defaults?.baseURL || "";

    // حذف /v1 از انتهای baseURL
    const origin = apiBase
      .replace(/\/v1\/?$/, "")
      .replace(/\/$/, "");

    // حذف / ابتدای مسیر
    const cleanedPath = receiptPath.replace(/^\//, "");

    // اگر cdn نداشت، اضافه کن
    const normalizedPath = cleanedPath.startsWith("cdn/")
      ? cleanedPath
      : `cdn/${cleanedPath}`;

    return `${origin}/${normalizedPath}`;
  };

  // =========================
  // Format Amount
  // =========================

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("fa-IR").format(
      Number(amount) || 0
    );
  };

  // =========================
  // Status Info
  // =========================

  const getStatusInfo = (status) => {
    switch (status) {
      case "pending":
        return {
          label: "در حال بررسی",
          color:
            "bg-yellow-100 text-yellow-800 border-yellow-200",
          icon: Clock,
        };

      case "approved":
        return {
          label: "تأیید شده",
          color:
            "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
        };

      case "completed":
        return {
          label: "تسویه شده",
          color:
            "bg-green-100 text-green-800 border-green-200",
          icon: CheckCircle,
        };

      case "rejected":
        return {
          label: "رد شده",
          color:
            "bg-red-100 text-red-800 border-red-200",
          icon: XCircle,
        };

      default:
        return {
          label: "نامشخص",
          color:
            "bg-gray-100 text-gray-800 border-gray-200",
          icon: Clock,
        };
    }
  };

  // =========================
  // Type Info
  // =========================

  const getTypeInfo = (type) => {
    return type === "automatic"
      ? {
        label: "خودکار",
        color:
          "bg-blue-100 text-blue-800 border-blue-200",
        icon: RefreshCw,
      }
      : {
        label: "دستی",
        color:
          "bg-purple-100 text-purple-800 border-purple-200",
        icon: CreditCard,
      };
  };

  // =========================
  // Pending Requests API
  // =========================

  const getRequests = async () => {
    if (!accessToken) return;

    try {
      setLoading(true);

      const response =
        await getPendingSettlements(accessToken);

      if (response?.ok) {
        const data = response?.data || [];

        const mappedData = data.map((item) => ({
          id: item.id,
          businessId: item.businessId,

          date: item.createdAt
            ? new Date(
              item.createdAt
            ).toLocaleString("fa-IR")
            : "-",

          type: item.method || "manual",

          shabaNumber:
            item.iban || "-",

          cardNumber:
            item.cardNumber || "-",

          username:
            item.username ||
            item.userName ||
            "-",

          businessName:
            item.businessName || "-",

          amount:
            Number(
              item.requestedAmount
            ) || 0,

          status:
            item.status || "pending",

          commissionAmount:
            item.commissionAmount,

          taxAmount:
            item.taxAmount,

          netAmount:
            item.netAmount,

          receiptImageUrl:
            item.receiptImageUrl || "",

          reviewNote:
            item.reviewNote,

          reviewedBy:
            item.reviewedBy,

          createdAt:
            item.createdAt,

          updatedAt:
            item.updatedAt,

          raw: item,
        }));

        setRequests(mappedData);
      } else {
        setRequests([]);
      }
    } catch (error) {
      console.error(
        "خطا در دریافت درخواست‌ها:",
        error
      );

      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // History API
  // =========================

  const getHistoryRequests = async () => {
    if (!accessToken) return;

    try {
      setHistoryLoading(true);

      const response =
        await axiosInstance.get(
          "/admin/settlements/list",
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

      const data =
        response?.data?.data ||
        response?.data ||
        [];

      const mappedData = Array.isArray(data)
        ? data.map((item) => ({
          id: item.id,

          businessId:
            item.businessId,

          date: item.createdAt
            ? new Date(
              item.createdAt
            ).toLocaleString(
              "fa-IR"
            )
            : "-",

          type:
            item.method ||
            "manual",

          shabaNumber:
            item.iban || "-",

          cardNumber:
            item.cardNumber || "-",

          username:
            item.username ||
            item.userName ||
            "-",

          businessName:
            item.businessName ||
            "-",

          amount:
            Number(
              item.requestedAmount
            ) || 0,

          requestedAmount:
            Number(
              item.requestedAmount
            ) || 0,

          commissionAmount:
            Number(
              item.commissionAmount
            ) || 0,

          taxAmount:
            Number(
              item.taxAmount
            ) || 0,

          netAmount:
            Number(
              item.netAmount
            ) || 0,

          status:
            item.status ||
            "pending",

          receiptImageUrl:
            item.receiptImageUrl ||
            "",

          reviewNote:
            item.reviewNote ||
            "",

          reviewedBy:
            item.reviewedBy ||
            "",

          createdAt:
            item.createdAt,

          updatedAt:
            item.updatedAt,

          raw: item,
        }))
        : [];

      setHistoryRequests(mappedData);
      setHistoryPage(1);
    } catch (error) {
      console.error(
        "خطا در دریافت تاریخچه تسویه:",
        error
      );

      console.error(
        "History API error response =>",
        error?.response?.data
      );

      setHistoryRequests([]);

      toast.error(
        error?.response?.data?.message ||
        "خطا در دریافت تاریخچه تسویه"
      );
    } finally {
      setHistoryLoading(false);
    }
  };

  // =========================
  // Initial Load
  // =========================

  useEffect(() => {
    if (!accessToken) return;

    getRequests();
    getHistoryRequests();
  }, [accessToken]);

  // =========================
  // Pending Filters
  // =========================

  const filteredRequests = requests.filter(
    (request) => {
      const search =
        searchTerm.toLowerCase();

      const matchSearch =
        String(
          request.username || ""
        )
          .toLowerCase()
          .includes(search) ||
        String(
          request.businessName || ""
        )
          .toLowerCase()
          .includes(search) ||
        String(
          request.shabaNumber || ""
        )
          .toLowerCase()
          .includes(search) ||
        String(
          request.cardNumber || ""
        )
          .toLowerCase()
          .includes(search);

      const matchStatus =
        statusFilter === "all" ||
        request.status === statusFilter;

      const matchType =
        typeFilter === "all" ||
        request.type === typeFilter;

      return (
        matchSearch &&
        matchStatus &&
        matchType
      );
    }
  );

  const manualRequests =
    filteredRequests.filter(
      (item) =>
        item.type === "manual"
    );

  const automaticRequests =
    filteredRequests.filter(
      (item) =>
        item.type === "automatic"
    );

  // =========================
  // History Filters
  // =========================

  const filteredHistoryRequests =
    historyRequests.filter(
      (request) => {
        const search =
          historySearchTerm
            .toLowerCase()
            .trim();

        const matchSearch =
          !search ||
          String(
            request.username || ""
          )
            .toLowerCase()
            .includes(search) ||
          String(
            request.businessName ||
            ""
          )
            .toLowerCase()
            .includes(search) ||
          String(
            request.shabaNumber ||
            ""
          )
            .toLowerCase()
            .includes(search) ||
          String(
            request.cardNumber || ""
          )
            .toLowerCase()
            .includes(search) ||
          String(
            request.id || ""
          )
            .toLowerCase()
            .includes(search);

        const matchStatus =
          historyStatusFilter ===
          "all" ||
          request.status ===
          historyStatusFilter;

        const matchType =
          historyTypeFilter ===
          "all" ||
          request.type ===
          historyTypeFilter;

        return (
          matchSearch &&
          matchStatus &&
          matchType
        );
      }
    );

  // =========================
  // History Pagination
  // =========================

  const historyTotalPages =
    Math.max(
      1,
      Math.ceil(
        filteredHistoryRequests.length /
        historyPageSize
      )
    );

  const historyStartIndex =
    (historyPage - 1) *
    historyPageSize;

  const paginatedHistoryRequests =
    filteredHistoryRequests.slice(
      historyStartIndex,
      historyStartIndex +
      historyPageSize
    );

  const historyPageNumbers =
    Array.from(
      {
        length: historyTotalPages,
      },
      (_, index) => index + 1
    );

  // وقتی فیلتر تغییر کرد
  useEffect(() => {
    setHistoryPage(1);
  }, [
    historySearchTerm,
    historyStatusFilter,
    historyTypeFilter,
  ]);

  // =========================
  // Approve Modal
  // =========================

  const openApproveModal = (
    request
  ) => {
    setSelectedRequest(request);
    setSelectedFile(null);
    setReceiptImageUrl("");
    setPaymentInfoText("");
    setApproveModal(true);
  };

  const closeApproveModal = () => {
    if (
      uploadingReceipt ||
      approving
    ) {
      return;
    }

    setApproveModal(false);
    setSelectedRequest(null);
    setSelectedFile(null);
    setReceiptImageUrl("");
    setPaymentInfoText("");
  };

  // =========================
  // File Change
  // =========================

  const handleFileChange = (e) => {
    const file =
      e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(
        "لطفاً یک فایل تصویری انتخاب کنید"
      );

      e.target.value = "";
      return;
    }

    if (
      file.size >
      5 * 1024 * 1024
    ) {
      toast.error(
        "حجم تصویر نباید بیشتر از ۵ مگابایت باشد"
      );

      e.target.value = "";
      return;
    }

    setSelectedFile(file);
    setReceiptImageUrl("");
  };

  // =========================
  // Upload Receipt
  // =========================

  const handleUploadReceipt =
    async () => {
      if (!selectedFile) {
        toast.error(
          "ابتدا تصویر رسید را انتخاب کنید"
        );

        return null;
      }

      try {
        setUploadingReceipt(true);

        const response =
          await uploadSettlementReceipt(
            accessToken,
            selectedFile
          );

        if (
          !response?.ok ||
          !response?.data?.url
        ) {
          throw new Error(
            response?.message ||
            "آپلود رسید ناموفق بود"
          );
        }

        const url =
          response.data.url;

        setReceiptImageUrl(url);

        toast.success(
          "رسید با موفقیت بارگذاری شد"
        );

        return url;
      } catch (error) {
        console.error(
          "Upload receipt error:",
          error
        );

        toast.error(
          error?.response?.data
            ?.message ||
          error?.message ||
          "خطا در بارگذاری رسید"
        );

        return null;
      } finally {
        setUploadingReceipt(false);
      }
    };

  // =========================
  // Approve
  // =========================

  const handleApprove = async () => {
    if (!selectedRequest) {
      return;
    }

    if (!selectedFile) {
      toast.error(
        "لطفاً تصویر رسید را انتخاب کنید"
      );

      return;
    }

    if (!paymentInfoText.trim()) {
      toast.error(
        "لطفاً اطلاعات پرداخت را وارد کنید"
      );

      return;
    }

    try {
      setApproving(true);

      let uploadedUrl =
        receiptImageUrl;

      if (!uploadedUrl) {
        uploadedUrl =
          await handleUploadReceipt();

        if (!uploadedUrl) {
          return;
        }
      }

      const response =
        await approveSettlement(
          accessToken,
          selectedRequest.id,
          uploadedUrl,
          paymentInfoText.trim()
        );

      if (!response?.ok) {
        throw new Error(
          response?.message ||
          "تأیید درخواست ناموفق بود"
        );
      }

      toast.success(
        "درخواست تسویه با موفقیت تأیید شد"
      );

      closeApproveModal();

      await getRequests();
      await getHistoryRequests();
    } catch (error) {
      console.error(
        "Approve settlement error:",
        error
      );

      toast.error(
        error?.response?.data
          ?.message ||
        error?.message ||
        "خطا در تأیید درخواست"
      );
    } finally {
      setApproving(false);
    }
  };

  // =========================
  // Details
  // =========================

  const showDetails = (
    request
  ) => {
    setSelectedRequest(request);
  };

  // =========================
  // Receipt
  // =========================

  const showReceipt = (request) => {
    if (!request?.receiptImageUrl) {
      toast.error("برای این درخواست رسیدی ثبت نشده است");
      return;
    }

    const fullUrl = getReceiptUrl(request.receiptImageUrl);

    setSelectedReceiptUrl(fullUrl);
    setReceiptModal(true);
  };

  const closeReceiptModal =
    () => {
      setReceiptModal(false);
      setSelectedReceiptUrl("");
    };

  // =========================
  // Table
  // =========================

  const renderTable = (
    data,
    isManual = false
  ) => {
    if (data.length === 0) {
      return (
        <div className="py-12 text-center text-gray-500">
          درخواست موردی وجود ندارد
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                تاریخ
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                شماره شبا
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                شماره کارت
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                درخواست‌دهنده
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                مبلغ
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                وضعیت
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                عملیات
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-200">
            {data.map(
              (request) => {
                const statusInfo =
                  getStatusInfo(
                    request.status
                  );

                const StatusIcon =
                  statusInfo.icon;

                return (
                  <tr
                    key={
                      request.id
                    }
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-4 text-sm text-gray-700 text-center whitespace-nowrap">
                      {
                        request.date
                      }
                    </td>

                    <td
                      className="px-4 py-4 text-sm text-gray-700 font-mono text-center"
                      dir="ltr"
                    >
                      {
                        request.shabaNumber
                      }
                    </td>

                    <td
                      className="px-4 py-4 text-sm text-gray-700 font-mono text-center"
                      dir="ltr"
                    >
                      {
                        request.cardNumber
                      }
                    </td>

                    <td className="px-4 py-4 text-sm text-gray-700 text-center">
                      <div>
                        <div className="font-medium">
                          {
                            request.username
                          }
                        </div>

                        <div className="text-xs text-gray-500 mt-1">
                          {
                            request.businessName
                          }
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 text-sm text-gray-700 font-medium text-center whitespace-nowrap">
                      {formatAmount(
                        request.amount
                      )}{" "}
                      ریال
                    </td>

                    <td className="px-4 py-4 text-center">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${statusInfo.color}`}
                      >
                        <StatusIcon className="w-4 h-4" />

                        {
                          statusInfo.label
                        }
                      </span>
                    </td>

                    <td className="px-4 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() =>
                            showDetails(
                              request
                            )
                          }
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="مشاهده جزئیات"
                        >
                          <Eye className="w-5 h-5" />
                        </button>

                        {request.receiptImageUrl && (
                          <button
                            onClick={() =>
                              showReceipt(
                                request
                              )
                            }
                            className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="نمایش رسید"
                          >
                            <FileImage className="w-5 h-5" />
                          </button>
                        )}

                        {isManual &&
                          request.status ===
                          "pending" && (
                            <button
                              onClick={() =>
                                openApproveModal(
                                  request
                                )
                              }
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="تأیید و ثبت رسید"
                            >
                              <CheckCircle className="w-5 h-5" />
                            </button>
                          )}
                      </div>
                    </td>
                  </tr>
                );
              }
            )}
          </tbody>
        </table>
      </div>
    );
  };

  // =========================
  // History Table
  // =========================

  const renderHistoryTable =
    () => {
      if (
        paginatedHistoryRequests.length ===
        0
      ) {
        return (
          <div className="py-12 text-center text-gray-500">
            موردی در تاریخچه وجود ندارد
          </div>
        );
      }

      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  تاریخ
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  نوع
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  شماره شبا
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  شماره کارت
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  درخواست‌دهنده
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  مبلغ درخواست
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  مبلغ خالص
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  وضعیت
                </th>

                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">
                  عملیات
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {paginatedHistoryRequests.map(
                (request) => {
                  const statusInfo =
                    getStatusInfo(
                      request.status
                    );

                  const typeInfo =
                    getTypeInfo(
                      request.type
                    );

                  const StatusIcon =
                    statusInfo.icon;

                  return (
                    <tr
                      key={
                        request.id
                      }
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4 text-sm text-gray-700 text-center whitespace-nowrap">
                        {
                          request.date
                        }
                      </td>

                      <td className="px-4 py-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${typeInfo.color}`}
                        >
                          <typeInfo.icon className="w-3.5 h-3.5" />

                          {
                            typeInfo.label
                          }
                        </span>
                      </td>

                      <td
                        className="px-4 py-4 text-sm text-gray-700 font-mono text-center"
                        dir="ltr"
                      >
                        {
                          request.shabaNumber
                        }
                      </td>

                      <td
                        className="px-4 py-4 text-sm text-gray-700 font-mono text-center"
                        dir="ltr"
                      >
                        {
                          request.cardNumber
                        }
                      </td>

                      <td className="px-4 py-4 text-sm text-gray-700 text-center">
                        <div className="font-medium">
                          {
                            request.username
                          }
                        </div>

                        <div className="text-xs text-gray-500 mt-1">
                          {
                            request.businessName
                          }
                        </div>
                      </td>

                      <td className="px-4 py-4 text-sm text-gray-700 font-medium text-center whitespace-nowrap">
                        {formatAmount(
                          request.requestedAmount
                        )}{" "}
                        ریال
                      </td>

                      <td className="px-4 py-4 text-sm text-green-600 font-bold text-center whitespace-nowrap">
                        {formatAmount(
                          request.netAmount
                        )}{" "}
                        ریال
                      </td>

                      <td className="px-4 py-4 text-center">
                        <span
                          className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${statusInfo.color}`}
                        >
                          <StatusIcon className="w-4 h-4" />

                          {
                            statusInfo.label
                          }
                        </span>
                      </td>

                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() =>
                              showDetails(
                                request
                              )
                            }
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="مشاهده جزئیات"
                          >
                            <Eye className="w-5 h-5" />
                          </button>

                          {request.receiptImageUrl && (
                            <button
                              onClick={() =>
                                showReceipt(
                                  request
                                )
                              }
                              className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                              title="نمایش رسید"
                            >
                              <FileImage className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      );
    };

  return (
    <>
      {/* Header */}

      <nav className="border-b border-slate-100 dark:bg-zinc-800 print:hidden flex items-center fixed top-0 right-0 left-0 bg-white z-10 dark:border-zinc-700">
        <Header />
      </nav>

      {/* Sidebar */}

      <div className="vertical-menu rtl:right-0 fixed ltr:left-0 bottom-0 top-16 h-screen border-r bg-slate-50 border-gray-50 print:hidden dark:bg-zinc-800 dark:border-neutral-700 z-10">
        <AdminSidebar />
      </div>

      {/* Main */}

      <div className="md:mr-64 mr-0 mt-16 p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">

          {/* Page Header */}

          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                مدیریت درخواست‌های تسویه
              </h1>

              <p className="text-gray-600 dark:text-gray-400 mt-1">
                {filteredRequests.length}{" "}
                درخواست در انتظار بررسی
              </p>
            </div>

            <button
              onClick={() => {
                getRequests();
                getHistoryRequests();
              }}
              disabled={
                loading ||
                historyLoading
              }
              className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ||
                  historyLoading
                  ? "animate-spin"
                  : ""
                  }`}
              />

              بروزرسانی
            </button>
          </div>

          {/* =========================
                        Pending Filters
                    ========================= */}

          <div className="bg-gradient-to-r from-gray-100 to-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-wrap gap-4 items-center">

              <div className="flex-1 min-w-[250px] relative">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                <input
                  type="text"
                  placeholder="جستجوی نام کاربری، کسب‌وکار، شبا یا کارت..."
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={
                    searchTerm
                  }
                  onChange={(
                    e
                  ) =>
                    setSearchTerm(
                      e
                        .target
                        .value
                    )
                  }
                />
              </div>

              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={
                  statusFilter
                }
                onChange={(
                  e
                ) =>
                  setStatusFilter(
                    e
                      .target
                      .value
                  )
                }
              >
                <option value="all">
                  همه وضعیت‌ها
                </option>

                <option value="pending">
                  در حال بررسی
                </option>

                <option value="approved">
                  تأیید شده
                </option>

                <option value="completed">
                  تسویه شده
                </option>

                <option value="rejected">
                  رد شده
                </option>
              </select>

              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={
                  typeFilter
                }
                onChange={(
                  e
                ) =>
                  setTypeFilter(
                    e
                      .target
                      .value
                  )
                }
              >
                <option value="all">
                  همه نوع‌ها
                </option>

                <option value="manual">
                  دستی
                </option>

                <option value="automatic">
                  خودکار
                </option>
              </select>
            </div>
          </div>

          {/* =========================
                        Manual Requests
                    ========================= */}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">

            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  درخواست‌های تسویه دستی
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  درخواست‌هایی که نیاز به بررسی و ثبت رسید دارند
                </p>
              </div>

              <span className="px-3 py-1 rounded-full bg-teal-100 text-teal-800 text-sm font-medium">
                {
                  manualRequests.length
                }{" "}
                درخواست
              </span>
            </div>

            {loading ? (
              <div className="py-12 flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              renderTable(
                manualRequests,
                true
              )
            )}
          </div>

          {/* =========================
                        Automatic Requests
                    ========================= */}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">

            <div className="px-6 py-5 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  درخواست‌های تسویه خودکار
                </h2>

                <p className="text-sm text-gray-500 mt-1">
                  درخواست‌های تسویه خودکار
                </p>
              </div>

              <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm font-medium">
                {
                  automaticRequests.length
                }{" "}
                درخواست
              </span>
            </div>

            {loading ? (
              <div className="py-12 flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              renderTable(
                automaticRequests,
                false
              )
            )}
          </div>

          {/* =========================
                        History
                    ========================= */}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-8">

            <div className="px-6 py-5 border-b border-gray-200">

              <div className="flex items-center justify-between mb-5">

                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    تاریخچه درخواست‌های تسویه
                  </h2>

                  <p className="text-sm text-gray-500 mt-1">
                    تمام درخواست‌های ثبت‌شده
                  </p>
                </div>

                <span className="px-3 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                  {
                    filteredHistoryRequests.length
                  }{" "}
                  درخواست
                </span>

              </div>

              {/* History Filters */}

              <div className="flex flex-wrap gap-4 items-center">

                <div className="flex-1 min-w-[250px] relative">

                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />

                  <input
                    type="text"
                    placeholder="جستجوی نام کاربری، کسب‌وکار، شبا یا کارت..."
                    className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={
                      historySearchTerm
                    }
                    onChange={(
                      e
                    ) =>
                      setHistorySearchTerm(
                        e
                          .target
                          .value
                      )
                    }
                  />

                </div>

                <select
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  value={
                    historyStatusFilter
                  }
                  onChange={(
                    e
                  ) =>
                    setHistoryStatusFilter(
                      e
                        .target
                        .value
                    )
                  }
                >
                  <option value="all">
                    همه وضعیت‌ها
                  </option>

                  <option value="pending">
                    در حال بررسی
                  </option>

                  <option value="approved">
                    تأیید شده
                  </option>

                  <option value="completed">
                    تسویه شده
                  </option>

                  <option value="rejected">
                    رد شده
                  </option>
                </select>

                <select
                  className="px-4 py-2 border border-gray-300 rounded-lg"
                  value={
                    historyTypeFilter
                  }
                  onChange={(
                    e
                  ) =>
                    setHistoryTypeFilter(
                      e
                        .target
                        .value
                    )
                  }
                >
                  <option value="all">
                    همه نوع‌ها
                  </option>

                  <option value="manual">
                    دستی
                  </option>

                  <option value="automatic">
                    خودکار
                  </option>
                </select>

              </div>
            </div>

            {/* History Table */}

            {historyLoading ? (
              <div className="py-12 flex justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : (
              renderHistoryTable()
            )}

            {/* Pagination */}

            {!historyLoading &&
              filteredHistoryRequests.length >
              0 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">

                  <div className="text-sm text-gray-500">
                    نمایش{" "}
                    <span className="font-medium text-gray-700">
                      {
                        historyStartIndex +
                        1
                      }
                    </span>{" "}
                    تا{" "}
                    <span className="font-medium text-gray-700">
                      {Math.min(
                        historyStartIndex +
                        historyPageSize,
                        filteredHistoryRequests.length
                      )}
                    </span>{" "}
                    از{" "}
                    <span className="font-medium text-gray-700">
                      {
                        filteredHistoryRequests.length
                      }
                    </span>{" "}
                    درخواست
                  </div>

                  <div className="flex items-center gap-1">

                    <button
                      onClick={() =>
                        setHistoryPage(
                          (prev) =>
                            Math.max(
                              1,
                              prev -
                              1
                            )
                        )
                      }
                      disabled={
                        historyPage ===
                        1
                      }
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>

                    {historyPageNumbers.map(
                      (
                        page
                      ) => (
                        <button
                          key={
                            page
                          }
                          onClick={() =>
                            setHistoryPage(
                              page
                            )
                          }
                          className={`min-w-[38px] h-[38px] px-3 rounded-lg border text-sm ${historyPage ===
                            page
                            ? "bg-blue-600 text-white border-blue-600"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                            }`}
                        >
                          {
                            page
                          }
                        </button>
                      )
                    )}

                    <button
                      onClick={() =>
                        setHistoryPage(
                          (
                            prev
                          ) =>
                            Math.min(
                              historyTotalPages,
                              prev +
                              1
                            )
                        )
                      }
                      disabled={
                        historyPage ===
                        historyTotalPages
                      }
                      className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>

                  </div>
                </div>
              )}
          </div>
        </div>

        <Footer />
      </div>

      {/* =========================
                Approve Modal
            ========================= */}

      {approveModal &&
        selectedRequest && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">

            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-lg"
              dir="rtl"
            >

              <div className="flex items-center justify-between px-6 py-4 border-b">

                <div>
                  <h2 className="text-lg font-bold text-gray-900">
                    تأیید درخواست تسویه
                  </h2>

                  <p className="text-sm text-gray-500 mt-1">
                    ثبت رسید پرداخت
                  </p>
                </div>

                <button
                  onClick={
                    closeApproveModal
                  }
                  disabled={
                    uploadingReceipt ||
                    approving
                  }
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-5">

                <div className="bg-gray-50 rounded-lg p-4">

                  <div className="grid grid-cols-2 gap-4">

                    <div>
                      <span className="text-xs text-gray-500">
                        مبلغ
                      </span>

                      <p className="font-bold text-gray-900 mt-1">
                        {formatAmount(
                          selectedRequest.amount
                        )}{" "}
                        ریال
                      </p>
                    </div>

                    <div>
                      <span className="text-xs text-gray-500">
                        نوع
                      </span>

                      <p className="font-medium text-gray-900 mt-1">
                        دستی
                      </p>
                    </div>

                    <div>
                      <span className="text-xs text-gray-500">
                        شماره کارت
                      </span>

                      <p
                        className="font-mono text-gray-900 mt-1"
                        dir="ltr"
                      >
                        {
                          selectedRequest.cardNumber
                        }
                      </p>
                    </div>

                    <div>
                      <span className="text-xs text-gray-500">
                        شبا
                      </span>

                      <p
                        className="font-mono text-gray-900 mt-1"
                        dir="ltr"
                      >
                        {
                          selectedRequest.shabaNumber
                        }
                      </p>
                    </div>

                  </div>
                </div>

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    تصویر رسید پرداخت
                  </label>

                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-xl p-6 cursor-pointer hover:bg-gray-50 transition-colors">

                    {selectedFile ? (
                      <>
                        <FileImage className="w-10 h-10 text-green-600 mb-2" />

                        <span className="text-sm font-medium text-gray-800 text-center break-all">
                          {
                            selectedFile.name
                          }
                        </span>

                        <span className="text-xs text-gray-500 mt-1">
                          {(
                            selectedFile.size /
                            1024 /
                            1024
                          ).toFixed(
                            2
                          )}{" "}
                          MB
                        </span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-gray-400 mb-2" />

                        <span className="text-sm text-gray-600">
                          انتخاب تصویر رسید
                        </span>

                        <span className="text-xs text-gray-400 mt-1">
                          JPG, PNG, WEBP
                        </span>
                      </>
                    )}

                    <input
                      type="file"
                      accept="image/*"
                      onChange={
                        handleFileChange
                      }
                      className="hidden"
                      disabled={
                        uploadingReceipt ||
                        approving
                      }
                    />

                  </label>

                  {receiptImageUrl && (
                    <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      رسید با موفقیت آپلود شد
                    </div>
                  )}

                </div>

                <div>

                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    اطلاعات پرداخت
                  </label>

                  <textarea
                    value={
                      paymentInfoText
                    }
                    onChange={(
                      e
                    ) =>
                      setPaymentInfoText(
                        e
                          .target
                          .value
                      )
                    }
                    rows={4}
                    placeholder="مثلاً: مبلغ ۴۵۵,۰۰۰ ریال به شماره کارت ...۶۷۸۹ واریز شد. کد پیگیری: 123456"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    disabled={
                      uploadingReceipt ||
                      approving
                    }
                  />

                </div>

                <div className="flex gap-3 pt-2">

                  <button
                    onClick={
                      closeApproveModal
                    }
                    disabled={
                      uploadingReceipt ||
                      approving
                    }
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  >
                    انصراف
                  </button>

                  <button
                    onClick={
                      handleApprove
                    }
                    disabled={
                      uploadingReceipt ||
                      approving ||
                      !selectedFile ||
                      !paymentInfoText.trim()
                    }
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {approving ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        در حال ثبت...
                      </>
                    ) : uploadingReceipt ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        در حال آپلود...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        ثبت و تأیید
                      </>
                    )}
                  </button>

                </div>

              </div>
            </div>
          </div>
        )}

      {/* =========================
                Details Modal
            ========================= */}

      {selectedRequest &&
        !approveModal &&
        !receiptModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">

            <div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl p-6"
              dir="rtl"
            >

              <div className="flex justify-between items-center mb-6">

                <h2 className="text-xl font-bold text-gray-900">
                  جزئیات درخواست تسویه
                </h2>

                <button
                  onClick={() =>
                    setSelectedRequest(
                      null
                    )
                  }
                  className="p-2 text-gray-400 hover:text-gray-700"
                >
                  <X className="w-5 h-5" />
                </button>

              </div>

              <div className="grid grid-cols-2 gap-5">

                <div>
                  <span className="text-sm text-gray-500">
                    تاریخ درخواست
                  </span>

                  <p className="font-medium mt-1">
                    {
                      selectedRequest.date
                    }
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    نوع درخواست
                  </span>

                  <p className="font-medium mt-1">
                    {
                      getTypeInfo(
                        selectedRequest.type
                      ).label
                    }
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    مبلغ درخواست
                  </span>

                  <p className="font-bold text-lg text-green-600 mt-1">
                    {formatAmount(
                      selectedRequest.requestedAmount ||
                      selectedRequest.amount
                    )}{" "}
                    ریال
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    مبلغ خالص
                  </span>

                  <p className="font-bold text-lg text-blue-600 mt-1">
                    {formatAmount(
                      selectedRequest.netAmount
                    )}{" "}
                    ریال
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    وضعیت
                  </span>

                  <p className="font-medium mt-1">
                    {
                      getStatusInfo(
                        selectedRequest.status
                      ).label
                    }
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    شماره شبا
                  </span>

                  <p
                    className="font-mono mt-1"
                    dir="ltr"
                  >
                    {
                      selectedRequest.shabaNumber
                    }
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    شماره کارت
                  </span>

                  <p
                    className="font-mono mt-1"
                    dir="ltr"
                  >
                    {
                      selectedRequest.cardNumber
                    }
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    کارمزد
                  </span>

                  <p className="font-medium mt-1">
                    {formatAmount(
                      selectedRequest.commissionAmount
                    )}{" "}
                    ریال
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    مالیات
                  </span>

                  <p className="font-medium mt-1">
                    {formatAmount(
                      selectedRequest.taxAmount
                    )}{" "}
                    ریال
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    نام کاربری
                  </span>

                  <p className="font-medium mt-1">
                    {
                      selectedRequest.username
                    }
                  </p>
                </div>

                <div>
                  <span className="text-sm text-gray-500">
                    کسب‌وکار
                  </span>

                  <p className="font-medium mt-1">
                    {
                      selectedRequest.businessName
                    }
                  </p>
                </div>

                {selectedRequest.reviewNote && (
                  <div className="col-span-2">
                    <span className="text-sm text-gray-500">
                      توضیحات بررسی
                    </span>

                    <p className="font-medium mt-1">
                      {
                        selectedRequest.reviewNote
                      }
                    </p>
                  </div>
                )}

              </div>

              {selectedRequest.receiptImageUrl && (
                <div className="mt-6 pt-5 border-t">

                  <button
                    onClick={() =>
                      showReceipt(
                        selectedRequest
                      )
                    }
                    className="w-full px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
                  >
                    <FileImage className="w-5 h-5" />

                    نمایش رسید پرداخت
                  </button>

                </div>
              )}

              {selectedRequest.type ===
                "manual" &&
                selectedRequest.status ===
                "pending" && (
                  <div className="mt-4 pt-5 border-t">

                    <button
                      onClick={() =>
                        openApproveModal(
                          selectedRequest
                        )
                      }
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2"
                    >
                      <CheckCircle className="w-5 h-5" />

                      تأیید و ثبت رسید
                    </button>

                  </div>
                )}

            </div>
          </div>
        )}

      {/* =========================
                Receipt Modal
            ========================= */}

      {receiptModal &&
        selectedReceiptUrl && (
          <div
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
            onClick={
              closeReceiptModal
            }
          >

            <div
              className="relative bg-white dark:bg-zinc-800 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
              dir="rtl"
              onClick={(e) =>
                e.stopPropagation()
              }
            >

              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-zinc-700">

                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                    رسید پرداخت
                  </h2>

                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    تصویر رسید ثبت‌شده برای درخواست تسویه
                  </p>
                </div>

                <button
                  onClick={
                    closeReceiptModal
                  }
                  className="p-2 text-gray-400 hover:text-gray-700 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-700"
                >
                  <X className="w-5 h-5" />
                </button>

              </div>

              <div className="p-6 bg-gray-100 dark:bg-zinc-900 flex items-center justify-center max-h-[75vh] overflow-auto">

                <img
                  src={
                    selectedReceiptUrl
                  }
                  alt="رسید پرداخت"
                  className="max-w-full max-h-[65vh] object-contain rounded-lg shadow-md"
                  onError={(
                    e
                  ) => {
                    console.error(
                      "Receipt image load error:",
                      selectedReceiptUrl
                    );

                    e.currentTarget.style.display =
                      "none";

                    toast.error(
                      "تصویر رسید قابل نمایش نیست"
                    );
                  }}
                />

              </div>

              <div className="px-6 py-4 border-t border-gray-200 dark:border-zinc-700 flex justify-between items-center gap-3">

                <div
                  className="text-xs text-gray-500 dark:text-gray-400 truncate"
                  dir="ltr"
                >
                  {
                    selectedReceiptUrl
                  }
                </div>

                <button
                  onClick={
                    closeReceiptModal
                  }
                  className="px-5 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-gray-700 dark:text-white rounded-lg"
                >
                  بستن
                </button>

              </div>

            </div>
          </div>
        )}
    </>
  );
};

export default SettlementRequestsList;